/**
 * VOSviewer's label placement, ported from `MapData.updateItemLabelScalingFactors`
 * and `calcLabelScalingFactor`.
 *
 * ## Why not collision detection
 *
 * The obvious way to label a crowded map is to walk the items in weight order
 * and skip any whose box hits a box already drawn.  That works, but it has to
 * be redone on every zoom step and it produces labels that pop in and out as
 * you move.  VOSviewer instead assigns each item, once, the **smallest zoom at
 * which its label stops colliding** — a single number per item, in pixels per
 * layout unit, the same units as the viewport scale.  Drawing is then a
 * comparison: show the label if `threshold <= currentScale`.  Zooming reveals
 * labels monotonically, panning reveals nothing new, and the whole thing costs
 * one number per item.
 *
 * For a pair of items the zoom at which their label boxes just touch is
 *
 * ```
 *   f(i, j) = min( 0.5 * (W_i + W_j + 2) / |x_i - x_j| ,
 *                  0.5 * (H_i + H_j + 2) / |y_i - y_j| )
 * ```
 *
 * — width and height in pixels at scale 1, coordinates in layout units, and
 * the `+ 2` a one-pixel gutter on each side.  Below `f` the boxes overlap;
 * above it they clear each other on at least one axis.
 *
 * ## The condition that does the work
 *
 * ```
 *   for i in items sorted by descending weight:
 *     t[i] = 0
 *     for j < i:
 *       f = f(i, j)
 *       if f > t[i] and f > t[j]:        // <-- this second test
 *         t[i] = f
 * ```
 *
 * `f > t[j]` asks whether item *j* is **already visible** at the zoom where *i*
 * would appear.  Dropping it — taking the plain maximum over all heavier items
 * — makes every item inherit the worst collision in the map, and the map shows
 * noticeably fewer labels: a light item sitting next to a heavy item that is
 * itself suppressed until 8x zoom would be held back to 8x for no reason, even
 * though at 3x there is nothing there to collide with.  With the test, its
 * threshold is whatever the *visible* neighbourhood demands.
 *
 * The heaviest item gets threshold 0 and is always labelled.  Two items at
 * identical coordinates give `f = Infinity`, so the lighter one is never
 * labelled; that is VOSviewer's behaviour and it is the right answer, since
 * the labels would be exactly on top of each other.
 */

import { LABEL_FADE_START, LABEL_MAX_ALPHA, LABEL_MIN_ALPHA } from './constants';

/**
 * Per-item label thresholds, in pixels per layout unit.
 *
 * `order` lists item indices sorted by descending weight; `frameW`/`frameH` are
 * the label box in pixels at scale 1, indexed by item.  The result is indexed
 * by item, not by rank.
 *
 * O(n^2) — 2 million pair tests at the 2000-item cap, roughly 10 ms, and it
 * runs once per data set rather than once per frame.
 */
export function computeLabelThresholds(
  order: Int32Array,
  x: Float64Array,
  y: Float64Array,
  frameW: Float64Array,
  frameH: Float64Array,
): Float64Array {
  const n = order.length;
  const threshold = new Float64Array(n);
  for (let rank = 0; rank < n; rank += 1) {
    const i = order[rank] as number;
    const xi = x[i] as number;
    const yi = y[i] as number;
    const wi = frameW[i] as number;
    const hi = frameH[i] as number;
    let best = 0;
    for (let other = 0; other < rank; other += 1) {
      const j = order[other] as number;
      const dx = Math.abs(xi - (x[j] as number));
      const dy = Math.abs(yi - (y[j] as number));
      // A zero separation on an axis means the boxes overlap on it at every
      // zoom: Infinity, which min() then discards unless both axes are zero.
      const fx = (0.5 * (wi + (frameW[j] as number) + 2)) / dx;
      const fy = (0.5 * (hi + (frameH[j] as number) + 2)) / dy;
      const f = fx < fy ? fx : fy;
      if (f > best && f > (threshold[j] as number)) best = f;
    }
    threshold[i] = best;
  }
  return threshold;
}

/**
 * Label opacity at the current zoom, from `VOSviewer.updateItemLabelAlpha`.
 *
 * At or above the threshold the label is fully drawn.  Between 0.6x and 1x the
 * threshold it fades in on a **quadratic** ramp, which is what makes zooming
 * feel like labels emerging rather than blinking: a linear ramp spends too
 * much of its range in the visible-but-unreadable middle.
 */
export function labelAlpha(threshold: number, scale: number): number {
  if (threshold <= scale) return LABEL_MAX_ALPHA;
  const fadeStart = LABEL_FADE_START * threshold;
  if (fadeStart < scale) {
    const t = (scale - fadeStart) / ((1 - LABEL_FADE_START) * threshold);
    return LABEL_MIN_ALPHA + (LABEL_MAX_ALPHA - LABEL_MIN_ALPHA) * t * t;
  }
  return 0;
}
